import classNames from "classnames/bind";
import React, { forwardRef } from "react";
import { Info } from "../Info";
import { displayValue } from "../utils/stringFormatter";
import styles from "./KeyValueList.module.scss";
import type { KeyValueListProps } from "./types";

const cx = classNames.bind(styles);

/**
 * A component to render a list of key-value pairs.
 */
const KeyValueList = forwardRef<HTMLDivElement, KeyValueListProps>(
  (
    {
      data = [],
      variant = "border",
      color = "neutral",
      maxRowCount,
      className,
      applySmallFont = false,
      isLoading = false,
      valueAlignment = "right",
    },
    ref
  ) => {
    const dataToDisplay = maxRowCount ? data.slice(0, maxRowCount) : data;

    return (
      <div
        ref={ref}
        className={cx(
          {
            shadedListContainer: variant === "shaded",
            [`shadedListContainer--${color}`]: variant === "shaded" && color,
          },
          className
        )}
      >
        {dataToDisplay.map((item, index) => {
          return (
            <div
              key={index}
              className={cx("itemContainer", {
                [`itemContainer--with-${variant}`]: variant,
                [`itemContainer--with-${variant}-${color}`]: variant && color,
              })}
            >
              <div
                className={cx({
                  key: true,
                  smallFont: applySmallFont,
                  skeleton: isLoading,
                })}
              >
                {item.title}
                {!!item.description && <Info content={item.description} />}
              </div>
              <div
                className={cx({
                  value: true,
                  valueAlignLeft: valueAlignment === "left",
                  smallFont: applySmallFont,
                  skeleton: isLoading,
                })}
              >
                {displayValue(item.value)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
);

KeyValueList.displayName = "KeyValueList";

export default KeyValueList;
